import React from 'react'
import PropTypes from 'prop-types'
import { Button, Typography, Row, Col } from 'antd'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useStores } from '../../cls/stores/MobXProvider'
import { runInAction, toJS } from 'mobx'
import { actSaveUser } from '../../stores/users/actions'
import { IS_NEW } from '../../config'
import { setCookie } from '../../config/utils'
import { observer } from 'mobx-react-lite'
import { useSmartTranslation } from '../../cls/TranslationContext'

/**
 * CookieConsentPopover component displays a popover requesting user consent for cookie usage.
 * It informs users about cookie usage and allows them to either agree or deny consent.
 * When the user makes a choice, it saves their preference in cookies and updates the user profile if logged in.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.visible - Determines if the popover should be displayed.
 * @param {function} props.setVisible - Function to update the visibility of the popover.
 * @returns {JSX.Element|null} The rendered popover if visible, otherwise null.
 *
 * @example
 * <CookieConsentPopover visible={isVisible} setVisible={setIsVisible} />
 */
const CookieConsentPopover = observer(({ visible, setVisible }) => {
	const { t } = useSmartTranslation()

	const { users } = useStores()
	const { loggedInUserId } = users

	// Save the consent preference in cookies and update user if logged in
	const handleConsent = (consent) => {
		setCookie('cookieConsent', consent, 365)
		setVisible(false)
		updateUserCookieConsent(consent)
	}

	// If user is logged in, update user cookie consent
	const updateUserCookieConsent = (consent) => {
		if (loggedInUserId) {
			const user = users.requireUserById(loggedInUserId)

			runInAction(() => {
				// Reset last full load
				user.loaded === IS_NEW && user.resetLastFullLoad()
				// set cookie consent value
				user.setCookieConsent(consent)
			})

			actSaveUser(user.user_id, toJS(user.full), toJS(user.allowed)).then(
				() => {
					users.saveState()
				}
			)
		}
	}
	if (!visible) return null

	return (
		<div className="privacy-popover">
			<Row
				align={'middle'}
				gutter={[40, 40]}
				style={{
					padding: '11px'
				}}
			>
				<Col>
					<Typography.Title level={3}>
						<b>{t('single.aggree.privacy-title')}</b>
					</Typography.Title>
					<Typography.Text>
						{t('single.agree.cookie-info')}
					</Typography.Text>
					<br />
					<Typography.Text>
						{t('single.agree.share-information')}
					</Typography.Text>
					<br />
					<Typography.Text>
						{t('single.agree.policy')}
					</Typography.Text>

					<Row
						style={{ marginTop: '16px', marginBottom: '19px' }}
						gutter={10}
					>
						<Col>
							<Button
								type="primary"
								icon={<CheckOutlined />}
								onClick={() => handleConsent(true)}
							>
								{t('single.agree.label')}
							</Button>
						</Col>
						<Col>
							<Button
								type="primary"
								danger
								icon={<CloseOutlined />}
								onClick={() => handleConsent(false)}
							>
								{t('single.deny')}
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>
		</div>
	)
})

CookieConsentPopover.propTypes = {
	visible: PropTypes.bool.isRequired,
	setVisible: PropTypes.func.isRequired
}

export default CookieConsentPopover
